/**
 * Viome handoff (section 1 of the order handoff).
 *
 * Viome sends a member to /pages/viome with:
 *   ref    vm_ + 26 Crockford base32 chars
 *   items  Shopify variant id : quantity pairs, comma-separated
 *
 * Example:
 *   /pages/viome?ref=vm_01J9K7Q4ZC8XW2P6R3T5V8NMEH&items=44567890123456:2,44567890987654:1
 *
 * The variant id is the numeric id /cart/add.js accepts (the variant's
 * legacy resource id). Airtable record ids and Shopify product ids are
 * skipped. Unknown variants are skipped. Quantity is 1–20, and only the
 * first 20 pairs are kept.
 *
 * The cart is filled in the background on this page. The selection is kept
 * in sessionStorage so the delivery-window step can put the same meals back
 * if something else cleared the cart, and can skip the preferences prefill
 * API. Checkout reads cart.items, so that cart is the order.
 */
(function (root) {
  var STORAGE_KEY = 'viomeHandoff';
  var REF_PATTERN = /^vm_[0-9A-HJKMNP-TV-Z]{26}$/i;
  var MAX_PAIRS = 20;
  var MIN_QTY = 1;
  var MAX_QTY = 20;

  function read() {
    try {
      var raw = root.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function write(handoff) {
    root.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handoff));
  }

  function variantIdFromToken(token) {
    var value = String(token || '').trim();
    var gid = value.match(/ProductVariant\/(\d+)$/);
    if (gid) return gid[1];
    if (/^\d{5,20}$/.test(value)) return value;
    return null;
  }

  function parseItems(raw) {
    if (!raw) return [];
    var pairs = String(raw).split(',');
    var items = [];
    var byId = {};

    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i].trim();
      if (!pair) continue;
      var separator = pair.lastIndexOf(':');
      if (separator <= 0) continue;

      var id = variantIdFromToken(pair.slice(0, separator));
      var qtyToken = pair.slice(separator + 1).trim();
      var quantity = parseInt(qtyToken, 10);
      if (!id || !/^\d+$/.test(qtyToken)) continue;
      if (quantity < MIN_QTY || quantity > MAX_QTY) continue;

      if (byId[id]) {
        byId[id].quantity = Math.min(MAX_QTY, byId[id].quantity + quantity);
        continue;
      }
      if (items.length >= MAX_PAIRS) continue;

      var item = { id: id, quantity: quantity };
      byId[id] = item;
      items.push(item);
    }

    return items;
  }

  function totalQuantity(items) {
    return items.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
  }

  function quantityMap(items) {
    var map = {};
    items.forEach(function (item) {
      var id = String(item.id);
      map[id] = (map[id] || 0) + Number(item.quantity);
    });
    return map;
  }

  function sameQuantities(left, right) {
    var leftKeys = Object.keys(left);
    var rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every(function (key) {
      return left[key] === right[key];
    });
  }

  function shopifyFetch(url, options) {
    var headers = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    if (options && options.body) headers['Content-Type'] = 'application/json';
    return fetch(url, {
      method: (options && options.method) || 'GET',
      credentials: 'same-origin',
      headers: headers,
      body: options && options.body
    });
  }

  async function loadCart() {
    var response = await shopifyFetch('/cart.js');
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  async function sellableItems(items) {
    var checks = await Promise.all(items.map(function (item) {
      return shopifyFetch('/variants/' + item.id + '.js')
        .then(function (response) {
          if (response.ok) return 'yes';
          if (response.status === 404) return 'no';
          return 'unknown';
        })
        .catch(function () { return 'unknown'; });
    }));
    return items.filter(function (item, index) {
      return checks[index] !== 'no';
    });
  }

  async function addItems(items) {
    var response = await shopifyFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: items.map(function (item) {
          return { id: item.id, quantity: item.quantity };
        })
      })
    });
    if (response.ok || items.length < 2) return response.ok;

    var anyAdded = false;
    for (var i = 0; i < items.length; i++) {
      var single = await shopifyFetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [{ id: items[i].id, quantity: items[i].quantity }]
        })
      });
      if (single.ok) anyAdded = true;
    }
    return anyAdded;
  }

  async function replaceCart(items) {
    var sellable = await sellableItems(items);
    if (!sellable.length) return [];

    await loadCart();

    try {
      await shopifyFetch('/cart/clear.js', { method: 'POST', body: '{}' });
    } catch (error) {
      console.warn('Unable to clear cart before Viome handoff:', error);
    }

    var added = await addItems(sellable);
    if (!added) return null;

    var cart = await loadCart();
    var desired = quantityMap(sellable);
    var actual = quantityMap(cart.items.map(function (item) {
      return { id: item.variant_id, quantity: item.quantity };
    }));

    if (!sameQuantities(desired, actual)) {
      var updates = {};
      Object.keys(actual).forEach(function (id) {
        updates[id] = 0;
      });
      Object.keys(desired).forEach(function (id) {
        updates[id] = desired[id];
      });
      var updateResponse = await shopifyFetch('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates: updates })
      });
      if (updateResponse.ok) {
        cart = await loadCart();
        actual = quantityMap(cart.items.map(function (item) {
          return { id: item.variant_id, quantity: item.quantity };
        }));
      }
    }

    var kept = sellable.filter(function (item) {
      return actual[item.id] === item.quantity;
    });
    return kept.length ? kept : null;
  }

  function rememberRef(handoff, ref) {
    if (!ref || !REF_PATTERN.test(ref)) return handoff;
    if (handoff.ref) return handoff;
    handoff.ref = 'vm_' + ref.slice(3).toUpperCase();
    handoff.refAt = new Date().toISOString();
    return handoff;
  }

  // custom.viome_ref on the Shopify order, only when this session arrived with a ref.
  function orderMetafields() {
    var handoff = read();
    if (!handoff || !handoff.ref) return [];
    return [{
      key: 'viome_ref',
      namespace: 'custom',
      value: String(handoff.ref)
    }];
  }

  async function ensureCart() {
    var handoff = read();
    if (!handoff || !Array.isArray(handoff.items) || !handoff.items.length) return false;

    try {
      var cart = await loadCart();
      var desired = quantityMap(handoff.items);
      var actual = quantityMap(cart.items.map(function (item) {
        return { id: item.variant_id, quantity: item.quantity };
      }));

      if (!sameQuantities(desired, actual)) {
        var added = await replaceCart(handoff.items);
        if (!added) return false;
        if (!added.length) {
          handoff.items = [];
          write(handoff);
          return false;
        }
        handoff.items = added;
        handoff.total = totalQuantity(added);
        write(handoff);
      }

      // The plan size is chosen on select-plan and has to survive checkout.
      // Do not replace it with the raw meal count, and do not add extra meals.
      return true;
    } catch (error) {
      console.warn('Viome cart handoff could not be applied:', error);
      return false;
    }
  }

  async function captureFromUrl() {
    var params = new URLSearchParams(root.location.search);
    var ref = params.get('ref') || '';
    var items = parseItems(params.get('items') || '');
    var handoff = rememberRef(read() || {}, ref);

    if (!items.length) {
      if (handoff.ref) write(handoff);
      return;
    }

    handoff.items = items;
    handoff.total = totalQuantity(items);
    write(handoff);
    root.sessionStorage.removeItem('viomePlanMeals');
    await ensureCart();
  }

  function isViomeLanding() {
    return /\/pages\/viome\/?$/.test(root.location && root.location.pathname || '');
  }

  root.ViomeHandoff = {
    parseItems: parseItems,
    read: read,
    ensureCart: ensureCart,
    captureFromUrl: captureFromUrl,
    orderMetafields: orderMetafields
  };

  if (root.document && isViomeLanding()) {
    captureFromUrl();
  }
})(typeof window !== 'undefined' ? window : this);
